import React, { startTransition, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Grid, Card, CardContent, FormControl, Box, TextField } from '@mui/material';
import Button from '@mui/material/Button';
import { useTranslation } from 'react-i18next';
import MenuItem from '@mui/material/MenuItem';
import { useAppSelector } from '../hooks/useAppSelector';
import { selectAllCompanies } from '../../slices/companies/companiesSlice';
import { SupplierWithId } from '../../models/supplier.model';
import { CustomerWithId } from '../../models/customer.model';
import { selectUser } from '../../slices/user/userSlice';
import { ContractGeneratorProps } from './types';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import { useContractsGenerator } from './hooks/useContractsGenerator';
import { PacmanLoaderBlock } from '../pacman-loader';

import { selectAllEmployees } from '../../slices/employees/employeesSlice';
import { selectAllSuppliers } from '../../slices/suppliers/suppliersSlice';
import { selectAllCustomers } from '../../slices/customers/customersSlice';
import {
  BusinessProfileContacts,
  selectBusinessProfileContacts,
} from '../../slices/business-profile/businessProfileSlice';
import { ProgressOverlay } from '../progress-overlay/ProgressOverlay';
import { Employee } from '../../models/employee.model';
import { Helpers } from '../helpers/helpers';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import styles from './ContractGenerator.module.scss';
import { EMPLOYEE_MANUAL_INSTRUCTION_STEPS, isEmployeeManualTemplate } from './employeeManual';

const ContractGeneratorDialog = ({
  libraryItem,
  setContractCreationEnabled,
}: ContractGeneratorProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const generateDocumentParam = params.get('generateDocument');
  const company = useAppSelector(selectAllCompanies)[0];
  const currentUser = useAppSelector(selectUser);
  const employees = useAppSelector(selectAllEmployees);
  const suppliers = useAppSelector(selectAllSuppliers);
  const customers = useAppSelector(selectAllCustomers);
  const businessProfileContacts = useAppSelector(selectBusinessProfileContacts);
  const employeesStatus = useAppSelector((state) => state.employees.status);
  const suppliersStatus = useAppSelector((state) => state.suppliers.status);
  const customersStatus = useAppSelector((state) => state.customers.status);
  const businessContactsStatus = useAppSelector((state) => state.businessProfile.status);
  const employeesSorted = employees
    ?.slice()
    .sort((a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? ''));
  const suppliersSorted = suppliers
    ?.slice()
    .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
  const customersSorted = customers
    ?.slice()
    .sort((a, b) => (a.customer_name ?? '').localeCompare(b.customer_name ?? ''));

  const showEmployeeManualInstructions = isEmployeeManualTemplate(libraryItem);

  const businessProfileContactsSorted = businessProfileContacts
    ?.slice()
    .sort((a, b) => a.contact_name.localeCompare(b.contact_name));

  const {
    aiContract,
    secondParty,
    handleGenerateTemplate,
    setSecondParty,
    secondPartyCategory,
    setSecondPartyCategory,
    analysisLoading,
    placeholders,
    setPlaceholders,
    step,
    setStep,
    createDocumentFromTemplate,
    checkingBusinessProfile,
  } = useContractsGenerator(
    libraryItem && libraryItem.category ? Helpers.lowercaseFirstLetter(libraryItem?.category) : ''
  );

  const getSecondParty = () => {
    switch (secondPartyCategory) {
      case 'employment':
        return employeesSorted;
      case 'customers':
        return customersSorted;
      case 'suppliers':
        return suppliersSorted;
      case 'business':
        return businessProfileContactsSorted;
      default:
        return [];
    }
  };

  const getSecondPartyName = (option: {
    first_name?: string;
    last_name?: string;
    full_name?: string;
    name?: string;
    customer_name?: string;
    contact_name?: string;
  }) => {
    switch (secondPartyCategory) {
      case 'employment':
        return option.full_name;
      case 'customers':
        return option.customer_name;
      case 'suppliers':
        return option.name;
      case 'business':
        return option.contact_name;
    }
  };

  useEffect(() => {
    if (libraryItem) {
      startTransition(() =>
        setSecondPartyCategory(Helpers.lowercaseFirstLetter(libraryItem.category!))
      );
    }
  }, [libraryItem, setSecondPartyCategory]);

  return (
    <>
      <Card sx={{ height: '100%' }}>
        <CardContent
          sx={{
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
            paddingBottom: '16px !important',
            paddingLeft: 0,
            paddingTop: 0,
          }}
        >
          <Box style={{ flexGrow: 1, overflow: 'auto', paddingTop: '6px' }}>
            {step === 1 && (
              <Grid component='div' container spacing={2}>
                <Grid component='div' size={{ xs: 12, md: 12 }}>
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel htmlFor='secondPartyCategorySelect' required={true}>
                      {t('labels.chooseProfile')}
                    </InputLabel>
                    <Select
                      id='secondPartyCategorySelect'
                      onChange={(e) => {
                        setSecondPartyCategory(e.target.value);
                        setSecondParty('noSecondParty');
                      }}
                      name='secondPartyCategory'
                      value={secondPartyCategory}
                      tabIndex={-1}
                      sx={{ width: '100%' }}
                    >
                      <MenuItem id='contractGeneratorHeaderMenuItem' value={'employment'}>
                        {t('employees.header')}
                      </MenuItem>
                      <MenuItem id='contractGeneratorCustomersMenuItem' value={'customers'}>
                        {t('Customers')}
                      </MenuItem>
                      <MenuItem id='contractGeneratorSuppliersMenuItem' value={'suppliers'}>
                        {t('documentVault.suppliers')}
                      </MenuItem>
                      <MenuItem id='contractGeneratorBusinessMenuItem' value={'business'}>
                        {t('documentVault.business')}
                      </MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid component='div' size={{ xs: 12, md: 12 }}>
                  <FormControl sx={{ width: '100%' }}>
                    <InputLabel htmlFor='secondPartySelect'>
                      {t('labels.chooseSecondParty')}
                    </InputLabel>
                    <Select
                      id='secondPartySelect'
                      onChange={(e) => {
                        setSecondParty(e.target.value);
                      }}
                      name='secondParty'
                      value={secondParty}
                      tabIndex={-1}
                      sx={{ width: '100%' }}
                    >
                      <MenuItem
                        id='contractGeneratorNoSecondPartyMenuItem'
                        key={'noSecondParty'}
                        value={'noSecondParty'}
                      >
                        {t('contractGeneratorDialog.noSecondParty')}
                      </MenuItem>
                      {getSecondParty()?.map(
                        (
                          option:
                            Employee | SupplierWithId | CustomerWithId | BusinessProfileContacts
                        ) => (
                          <MenuItem
                            id={`contractGeneratorSecondPartyMenuItem_${option.id}`}
                            key={option.id}
                            value={option.id}
                          >
                            {getSecondPartyName(option)}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>
                {showEmployeeManualInstructions && (
                  <Grid component='div' size={{ xs: 12, md: 12 }}>
                    <Box
                      component='section'
                      className={styles.manual_instructions}
                      aria-labelledby='employeeManualInstructionsTitle'
                    >
                      <div className={styles.manual_instructions_header}>
                        <h3
                          id='employeeManualInstructionsTitle'
                          className={styles.manual_instructions_title}
                        >
                          {t('contractGeneratorDialog.employeeManual.title')}
                        </h3>
                        <span className={styles.manual_instructions_badge}>
                          {t('contractGeneratorDialog.employeeManual.stepsCount', {
                            count: EMPLOYEE_MANUAL_INSTRUCTION_STEPS.length,
                          })}
                        </span>
                      </div>
                      <ol className={styles.manual_instructions_list}>
                        {EMPLOYEE_MANUAL_INSTRUCTION_STEPS.map((stepKey, index) => (
                          <li key={stepKey} className={styles.manual_instructions_step}>
                            <span className={styles.manual_instructions_number} aria-hidden='true'>
                              {index + 1}
                            </span>
                            <div>
                              <p className={styles.manual_instructions_step_title}>
                                {t(`contractGeneratorDialog.employeeManual.steps.${stepKey}.title`)}
                              </p>
                              <p className={styles.manual_instructions_step_text}>
                                {t(`contractGeneratorDialog.employeeManual.steps.${stepKey}.text`)}
                              </p>
                            </div>
                          </li>
                        ))}
                      </ol>
                      <div className={styles.manual_instructions_note}>
                        <InfoOutlinedIcon aria-hidden='true' />
                        <span>{t('contractGeneratorDialog.employeeManual.note')}</span>
                      </div>
                    </Box>
                  </Grid>
                )}
              </Grid>
            )}
            {step === 2 && (
              <Box>
                {Object.keys({ ...aiContract.placeholder_matches }).map((el, i) => {
                  return (
                    <Box
                      key={Object.keys({ ...aiContract.placeholder_matches })[i]}
                      style={{ marginBottom: '16px' }}
                    >
                      <TextField
                        fullWidth
                        label={Object.keys({ ...aiContract.placeholder_matches })[i]}
                        variant='outlined'
                        size='medium'
                        onChange={(e) => setPlaceholders({ ...placeholders, [el]: e.target.value })}
                        value={placeholders[el]}
                      />
                    </Box>
                  );
                })}
              </Box>
            )}
          </Box>
          <Grid
            component='div'
            container
            style={{
              paddingTop: '16px',
              borderTop: '1px solid #dedede',
              marginTop: 'auto',
              justifyContent: 'flex-end',
              flexShrink: '0',
              gap: '8px',
            }}
          >
            <Button
              id='contractGeneratorGoBackBtn'
              variant='contained'
              onClick={() => {
                if (step === 1) {
                  setContractCreationEnabled(false);
                  if (generateDocumentParam) {
                    navigate(window.location.pathname);
                  }
                } else if (step === 2) {
                  setStep(1);
                }
              }}
              className='btn-default btn-action-basic btn-cancel'
            >
              {t('buttons.goBack')}
            </Button>
            <Button
              id='contractGeneratorNextBtn'
              aria-label={t('buttons.next')}
              variant='contained'
              disabled={!secondPartyCategory}
              onClick={() => {
                if (!libraryItem) return;
                if (step === 1) {
                  const data = {
                    user: currentUser?.id,
                    ['document_upload' in libraryItem ? 'document_template' : 'document_library']:
                      libraryItem.id,
                    company_id: company?.id ?? 0,
                    category: secondPartyCategory,
                  };
                  if (secondParty !== 'noSecondParty') {
                    data.secondary_id = Number(secondParty);
                  }
                  handleGenerateTemplate(libraryItem, data);
                } else if (step === 2) {
                  createDocumentFromTemplate(libraryItem);
                }
              }}
              className='btn-default btn-action-basic'
            >
              {t('buttons.next')}
            </Button>
          </Grid>
        </CardContent>
      </Card>
      {analysisLoading && <PacmanLoaderBlock loading={analysisLoading} />}
      {checkingBusinessProfile ||
        ((employeesStatus === 'loading' ||
          suppliersStatus === 'loading' ||
          customersStatus === 'loading' ||
          businessContactsStatus === 'loading') && <ProgressOverlay position='absolute' />)}
    </>
  );
};

export default ContractGeneratorDialog;
